import * as React from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Input } from "../../bits/Input/Input";
import styles from "./ManageClinician.module.scss";
import { CacheContext } from "../../../../contexts/CacheContext";
import { AclContext } from "../../../../contexts/AclContext";
import { defaultOffices } from "../../../../store/cache/location";
import { Select } from "../../bits/Select/Select";
import {
    ClinicianCredential,
    IClinician,
    IClinicianStatusChange,
    IExperienceLevelOption,
    SupervisorPair,
} from "intake_shared/src/types";
import {
    findLabelByPracticeId,
    formatLegacyPhoneNumber,
} from "../../../../utils/utils";
import { cn, copyToClipboard } from "../../../helpers";
import PhoneInput from "../../bits/PhoneInput/PhoneInput";
import { DepartmentCache } from "../../../../store/cache/requested_service";
import store from "../../../../store";
import { useToasts } from "react-toast-notifications";
import { Button } from "../../bits/Button/Button";
import { Badge } from "../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../constants";
import { FormCheckbox } from "../../bits/FormCheckbox/FormCheckbox";
import { TextArea } from "../../bits/TextArea/TextArea";
import { DatePicker } from "../../bits/DatePicker/DatePicker";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import moment from "moment";
import { httpsCallable } from "firebase/functions";
import { fxs } from "../../../../utils/firestore";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import Table from "../../bits/Table/Table";
import CancelIcon from "../../icons/Cancel";
import ChevronDownIcon from "../../icons/ChevronDown";
import CopyIcon from "../../icons/Copy";
import { InfoIcon } from "../../icons/Info";
import { SearchInput } from "../../bits/SearchInput/SearchInput";
import FilterButton from "../../bits/FilterButton/FilterButton";
import SearchIcon from "../../icons/Search";
import TablePagination from "../../bits/Table/TablePagination";
import CredentialExpirationStatus from "../CredentialExpirationStatus/CredentialExpirationStatus";
import ClinicianStatusHistory from "../ClinicianStatusHistory/ClinicianStatusHistory";
import { getClinicianCaseloadStats } from "../../../v2utils";

type Option = {
    label: string;
    value: string;
};

const PAGE_LIMIT = 25;

function getClinicianRowStyle(clinician: IClinician): Record<string, string> {
    if (clinician.booking_paused) {
        return { backgroundColor: "var(--tone-danger-bg)" };
    }

    if (clinician.marked_as_priority) {
        return { backgroundColor: "var(--tone-success-bg)" };
    }

    return {};
}

const defaultClinician: IClinician = {
    email: "",
    fullname: "",
    firstname: "",
    lastname: "",
    contact_info_client: "",
    contact_info_personal: "",
    locations: ["Brooklyn"],
    npi: "",
    credential: ClinicianCredential.PHD,
    methodology: [],
    personality_notes: "",
    dob: new Date(),
    start_date: new Date(),
    is_supervisor: false,
    office_windows: [],
    available: [],
    unavailable: [],
    held: [],
    confirmed: [],
    supervisor_id: "",
    do_not_show_on_website: false,
    do_not_sync_to_zocdoc: false,
    experience_levels: {},
    updated_at: new Date(),
    started_practicing: new Date(),
    booking_paused: false,
    marked_as_priority: false,
    departments: ["HT"],
};

export default function ManageClinician() {
    const history = useHistory();
    const olc = React.useContext(CacheContext);
    const aclContext = React.useContext(AclContext);
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const [pageNumber, setPageNumber] = React.useState(0);
    const [actionsClinicianEmail, setActionsClinicianEmail] = React.useState<
        string | null
    >(null);
    const { addToast } = useToasts();
    const [clinicianData, setClinicianData] = React.useState(defaultClinician);
    const [clinicianList, setClinicianList] = React.useState([] as IClinician[]);
    const [allSupervisors, setAllSupervisors] = React.useState([] as IClinician[]);
    const [searchClinicianName, setSearchClincianName] = React.useState("");
    const [clinicianInView, setClinicianInView] = React.useState<IClinician>();
    const [statusChanges, setStatusChanges] = React.useState<
        IClinicianStatusChange[]
    >([]);
    const [isEditorOpen, setIsEditorOpen] = React.useState(false);
    const [priorityChangeNote, setPriorityChangeNote] = React.useState("");
    const wasBookingsPaused =
        clinicianInView?.email === clinicianData.email &&
        Boolean(clinicianInView?.booking_paused);
    const originalPriority =
        clinicianInView?.email === clinicianData.email
            ? Boolean(clinicianInView.marked_as_priority)
            : false;
    const priorityWasChanged =
        Boolean(clinicianData.marked_as_priority) !== originalPriority;
    const [supervisorPracticePairs, setSupervisorPracticePairs] = React.useState<
        SupervisorPair[]
    >([]);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [methodologyOptions, setMethodologyOptions] = React.useState<string[]>([]);
    const [allExperienceLevelOptions, setAllExperienceLevelOptions] = React.useState<
        IExperienceLevelOption[]
    >([]);
    const allDepartmentsAndServices =
        olc?.allDepartmentAndServices || ({} as DepartmentCache);

    const fetchClinicians = React.useCallback(
        async (
            afterId?: string,
            beforeId?: string,
            pageNumber = 0,
            pageLimit = PAGE_LIMIT
        ) => {
            let clinicians: IClinician[] = await store.fetchAllCliniciansData(
                afterId,
                beforeId,
                pageLimit
            );
            let supervisors = await store.fetchAllSupervisors();
            setClinicianList(clinicians);
            setPageNumber(pageNumber);
            setAllSupervisors(supervisors);
        },
        []
    );

    function getFilteredDepartments(): DepartmentCache {
        if (clinicianInView) {
            const { supervisor_id, secondary_supervisor_ids, departments } =
                clinicianInView;

            const departmentsToRemove = new Set<string>();

            if (supervisor_id) {
                departmentsToRemove.add(departments[0]);
            }

            if (secondary_supervisor_ids && secondary_supervisor_ids.length > 0) {
                secondary_supervisor_ids.forEach((supervisor) => {
                    const departmentEntry = Object.entries(
                        allDepartmentsAndServices
                    ).find(
                        ([, value]) => value.practice_id === supervisor.practice_id
                    );
                    if (departmentEntry) {
                        const [department] = departmentEntry;
                        departmentsToRemove.add(department);
                    }
                });
            }

            // Filter out the departments to be removed and retain the structure
            const filteredDepartments: DepartmentCache = Object.entries(
                allDepartmentsAndServices
            )
                .filter(([department]) => !departmentsToRemove.has(department))
                .reduce((result: DepartmentCache, [department, services]) => {
                    result[department] = services;
                    return result;
                }, {});

            return filteredDepartments;
        }
        return {};
    }

    // Function to handle adding more supervisor-practice pairs
    const addMorePair = () => {
        setSupervisorPracticePairs([
            ...supervisorPracticePairs,
            { practice_id: "", supervisor_id: "" },
        ]);
    };

    // Function to handle updating supervisor ID
    const handleSupervisorChange = (index, value) => {
        const updatedPairs = [...supervisorPracticePairs];
        updatedPairs[index].supervisor_id = value;
        const existingPairs = clinicianInView?.secondary_supervisor_ids || [];
        updateClinicianDataState("secondary_supervisor_ids", [
            ...existingPairs,
            ...updatedPairs,
        ]);
        setSupervisorPracticePairs(updatedPairs);
    };

    // Function to handle updating practice ID
    const handlePracticeChange = (index, value) => {
        const updatedPairs = [...supervisorPracticePairs];
        updatedPairs[index].practice_id = value;
        const existingPairs = clinicianInView?.secondary_supervisor_ids || [];
        updateClinicianDataState("secondary_supervisor_ids", [
            ...existingPairs,
            ...updatedPairs,
        ]);
        setSupervisorPracticePairs(updatedPairs);
    };

    // Function to handle removing a supervisor-practice pair
    const removePair = (index: number) => {
        const updatedPairs = [...supervisorPracticePairs];
        updatedPairs.splice(index, 1);
        setSupervisorPracticePairs(updatedPairs);
        updateClinicianDataState("secondary_supervisor_ids", [
            ...(clinicianInView?.secondary_supervisor_ids || []),
        ]);
    };

    const locations: Option[] = Object.keys(availableOffices).map((office) => ({
        label: office,
        value: office,
    }));

    const credentials: Option[] = Object.keys(ClinicianCredential).map(
        (credential) => ({
            label: (ClinicianCredential as any)[credential],
            value: (ClinicianCredential as any)[credential],
        })
    );

    const supervisorOptions: Option[] = allSupervisors.map((supervisor) => ({
        label: supervisor.fullname,
        value: supervisor.email,
    }));

    const practiceOptions: Option[] = Object.entries(getFilteredDepartments()).map(
        ([department, services]) => ({
            label: services.label || "",
            value: services.practice_id || "",
        })
    );

    const getClinicianInView = async (email: string) => {
        setStatusChanges([]);
        const [clinician, changes] = await Promise.all([
            store.getClinicianByEmail(email),
            store.fetchClinicianStatusChanges(email),
        ]);
        setClinicianInView(clinician);
        setStatusChanges(changes);
    };

    const getPracticeName = (practice_id) => {
        for (const key in allDepartmentsAndServices) {
            if (allDepartmentsAndServices[key].practice_id === practice_id) {
                return allDepartmentsAndServices[key].label;
            }
        }
        return "";
    };

    const updateClinicianDataState = (
        label: keyof IClinician,
        value:
            | string
            | number
            | Location
            | ClinicianCredential
            | boolean
            | Date
            | SupervisorPair[]
            | Option[]
    ) => {
        if (value === undefined || value === null) {
            value = "";
        }
        let updatedClinicianData: IClinician;
        if (label === "locations") {
            const selectedLocations = (value as Option[]).map(
                (option) => option.value
            );
            updatedClinicianData = {
                ...clinicianData,
                locations: selectedLocations,
            };
        } else if (label === "methodology") {
            const current = clinicianData.methodology || [];
            const toggled = value as string;
            const updated = current.includes(toggled)
                ? current.filter((m) => m !== toggled)
                : [...current, toggled];
            updatedClinicianData = { ...clinicianData, methodology: updated };
        } else {
            updatedClinicianData = { ...clinicianData, [label]: value };
        }
        setClinicianData(updatedClinicianData);
    };

    // Validation function to check required fields
    const validateForm = () => {
        const requiredFields = {
            email: clinicianData.email?.trim(),
            firstname: clinicianData.firstname?.trim(),
            lastname: clinicianData.lastname?.trim(),
            credential: clinicianData.credential,
            locations: clinicianData.locations?.length > 0,
            npi: clinicianData.npi?.trim(),
        };

        const missingFields = Object.entries(requiredFields).filter(
            ([_, value]) => !value
        );

        // Check supervisor-practice pairs
        const hasIncompleteSupervisorPairs = supervisorPracticePairs.some(
            (pair) => pair.practice_id === "" || pair.supervisor_id === ""
        );

        return {
            isValid: missingFields.length === 0 && !hasIncompleteSupervisorPairs,
            missingFields: missingFields.map(([field]) => field),
            hasIncompleteSupervisorPairs,
        };
    };

    const submitClinicianData = async (event: React.FormEvent<HTMLElement>) => {
        event.preventDefault();

        const validation = validateForm();

        if (!validation.isValid) {
            const missingFieldNames = validation.missingFields
                .map((field) => field.charAt(0).toUpperCase() + field.slice(1))
                .join(", ");

            let errorMessage = "";
            if (validation.missingFields.length > 0) {
                errorMessage += `Please fill in all required fields: ${missingFieldNames}. `;
            }
            if (validation.hasIncompleteSupervisorPairs) {
                errorMessage += "Please complete all supervisor-practice pairs.";
            }

            addToast(errorMessage, {
                appearance: "error",
                autoDismiss: false,
            });
            return;
        }

        const loggedInUserEmail = aclContext?.authUser.user?.email;
        if (
            clinicianData.booking_paused &&
            !wasBookingsPaused &&
            !clinicianData.booking_pause_reason?.trim()
        ) {
            addToast("Please enter a reason for pausing bookings.", {
                appearance: "error",
            });
            return;
        }
        if (priorityWasChanged && !priorityChangeNote.trim()) {
            addToast("Please enter a note for changing priority.", {
                appearance: "error",
            });
            return;
        }
        if (!loggedInUserEmail) {
            addToast("Please sign in before saving the clinician.", {
                appearance: "error",
            });
            return;
        }

        try {
            const isClinicianNew = !(await store.doesClinicianExist(
                clinicianData.email
            ));
            await store.saveClinicianData(
                clinicianData,
                isClinicianNew,
                methodologyOptions,
                loggedInUserEmail,
                priorityChangeNote
            );
            addToast(`${clinicianData.fullname} Saved`, {
                appearance: "success",
            });
            setClinicianData(defaultClinician);
            setSupervisorPracticePairs([]);
            setPriorityChangeNote("");
            setClinicianInView(undefined);
            setStatusChanges([]);
            setIsEditorOpen(false);
            await fetchClinicians();
        } catch {
            addToast("Unable to save the clinician. Please try again.", {
                appearance: "error",
            });
        }
    };

    const deleteClinician = async () => {
        const loggedInUserEmail = aclContext?.authUser.user?.email || "";
        await store.deleteClinician(
            clinicianInView?.email || clinicianInView?.id || "",
            loggedInUserEmail
        );
        const filteredClinicians = [...clinicianList].filter(
            (c) => c.email !== clinicianInView?.email
        );
        setClinicianList(filteredClinicians);
        setShowDeleteDialog(false);
        addToast(`${clinicianInView?.fullname} Deleted`, {
            appearance: "success",
        });
    };

    const fetchOnlyPausedClinicians = async () => {
        let clinicians: IClinician[] = await store.findClinicianByField(
            "booking_paused",
            true
        );
        setClinicianList(clinicians);
        setPageNumber(0);
    };

    const fetchonlyPrioritizedClinicians = async () => {
        let clinicians: IClinician[] = await store.findClinicianByField(
            "marked_as_priority",
            true
        );
        setClinicianList(clinicians);
        setPageNumber(0);
    };

    const fetchonlyDoNotShowOnWebsiteClinicians = async () => {
        let clinicians: IClinician[] = await store.findClinicianByField(
            "do_not_show_on_website",
            true
        );
        setClinicianList(clinicians);
        setPageNumber(0);
    };

    const fetchonlyDoNotSyncToZocClinicians = async () => {
        let clinicians: IClinician[] = await store.findClinicianByField(
            "do_not_sync_to_zocdoc",
            true
        );
        setClinicianList(clinicians);
        setPageNumber(0);
    };

    const editClinician = (row: IClinician) => {
        setClinicianData(row);
        setClinicianInView(row);
        setPriorityChangeNote("");
        setIsEditorOpen(true);
        getClinicianInView(row.email);
    };

    const addClinician = () => {
        setClinicianData(defaultClinician);
        setClinicianInView(undefined);
        setSupervisorPracticePairs([]);
        setStatusChanges([]);
        setPriorityChangeNote("");
        setIsEditorOpen(true);
    };

    const closeEditor = () => {
        setClinicianData(defaultClinician);
        setClinicianInView(undefined);
        setSupervisorPracticePairs([]);
        setStatusChanges([]);
        setPriorityChangeNote("");
        setIsEditorOpen(false);
    };

    const columns = React.useMemo(() => {
        return [
            {
                Header: "Clinician's Name",
                accessor: "fullname",
                Cell: ({ row }: { row: { original: IClinician } }) => (
                    <div className={styles.clinicianNameCell}>
                        <span>{row.original.fullname}</span>
                        {row.original.marked_as_priority && (
                            <Badge content="Priority" tone="success" />
                        )}
                        {row.original.booking_paused && (
                            <Badge content="Paused" tone="danger" />
                        )}
                        <CredentialExpirationStatus
                            clinician={row.original}
                            experienceLevelOptions={allExperienceLevelOptions}
                        />
                    </div>
                ),
            },
            {
                Header: "Locations",
                id: "locations",
                accessor: (row: IClinician) => {
                    const officeWindows = row.office_windows;
                    const locations = officeWindows.map((window) => window.location);
                    const uniqueLocations = Array.from(new Set(locations));
                    return uniqueLocations.join(", ") || "--";
                },
            },
            {
                Header: "Credential",
                id: "credential",
                accessor: (row: IClinician) => (
                    <Badge
                        content={row.credential}
                        tone={
                            [
                                ClinicianCredential.MHCLP,
                                ClinicianCredential.LMSW,
                            ].includes(row.credential)
                                ? "warning"
                                : "info"
                        }
                    />
                ),
            },
            {
                Header: "NPI",
                accessor: "npi",
                Cell: ({ row }: { row: { original: IClinician } }) => {
                    const { npi } = row.original;
                    return npi ? (
                        <div className={styles.clinicianInfo}>
                            <span>{npi}</span>
                            <Button
                                variant="normal"
                                size="icon"
                                title="Copy NPI"
                                aria-label="Copy NPI"
                                onClick={() => copyToClipboard(npi)}
                            >
                                <CopyIcon />
                            </Button>
                        </div>
                    ) : (
                        "--"
                    );
                },
            },
            {
                Header: "email",
                accessor: "email",
                Cell: ({ row }: { row: { original: IClinician } }) => (
                    <div className={styles.clinicianInfo}>
                        <span>{row.original.email || "--"}</span>
                        {row.original.email && (
                            <Button
                                variant="normal"
                                size="icon"
                                title="Copy email"
                                aria-label="Copy email"
                                onClick={() => copyToClipboard(row.original.email)}
                            >
                                <CopyIcon />
                            </Button>
                        )}
                    </div>
                ),
            },
            {
                Header: "Caseload",
                id: "caseload",
                accessor: (row: IClinician) =>
                    getClinicianCaseloadStats(row).weeklySessions,
                Cell: ({ row }: { row: { original: IClinician } }) => {
                    const stats = getClinicianCaseloadStats(row.original);
                    const sortedEntities = stats.entities
                        .map((entity) => ({
                            ...entity,
                            label:
                                findLabelByPracticeId(
                                    entity.practiceId,
                                    allDepartmentsAndServices
                                ) ?? entity.practiceId,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label));
                    const tooltipId = `caseload-${
                        row.original.id ?? row.original.email
                    }`.replace(/[^a-zA-Z0-9-_]/g, "-");
                    return (
                        <div className={styles.caseloadSummary}>
                            <span className={styles.caseloadTotals}>
                                <OverlayTrigger
                                    placement="top"
                                    trigger={["hover", "focus"]}
                                    overlay={
                                        <Tooltip id={`${tooltipId}-clients`}>
                                            <div
                                                className={styles.practiceBreakdown}
                                            >
                                                <strong>Clients by practice</strong>
                                                {sortedEntities.length > 0 ? (
                                                    sortedEntities.map((entity) => (
                                                        <div key={entity.practiceId}>
                                                            {entity.clients}{" "}
                                                            {entity.label}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div>No clients</div>
                                                )}
                                            </div>
                                        </Tooltip>
                                    }
                                >
                                    <button
                                        type="button"
                                        className={styles.caseloadMetric}
                                        aria-label={`${stats.clients} clients, show practice breakdown`}
                                    >
                                        {stats.clients} clients
                                        <InfoIcon
                                            width="14"
                                            height="14"
                                            stroke="currentColor"
                                        />
                                    </button>
                                </OverlayTrigger>
                                <span aria-hidden="true">·</span>
                                <OverlayTrigger
                                    placement="top"
                                    trigger={["hover", "focus"]}
                                    overlay={
                                        <Tooltip id={`${tooltipId}-weekly`}>
                                            <div
                                                className={styles.practiceBreakdown}
                                            >
                                                <strong>
                                                    Weekly sessions by practice
                                                </strong>
                                                {sortedEntities.length > 0 ? (
                                                    sortedEntities.map((entity) => (
                                                        <div key={entity.practiceId}>
                                                            {entity.weeklySessions.toLocaleString(
                                                                "en-US",
                                                                {
                                                                    maximumFractionDigits: 2,
                                                                }
                                                            )}{" "}
                                                            {entity.label}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div>No weekly sessions</div>
                                                )}
                                            </div>
                                        </Tooltip>
                                    }
                                >
                                    <button
                                        type="button"
                                        className={styles.caseloadMetric}
                                        aria-label={`${stats.weeklySessions.toLocaleString(
                                            "en-US",
                                            { maximumFractionDigits: 2 }
                                        )} weekly, show practice breakdown`}
                                    >
                                        {stats.weeklySessions.toLocaleString(
                                            "en-US",
                                            { maximumFractionDigits: 2 }
                                        )}{" "}
                                        weekly
                                        <InfoIcon
                                            width="14"
                                            height="14"
                                            stroke="currentColor"
                                        />
                                    </button>
                                </OverlayTrigger>
                            </span>
                            <span>
                                {stats.percentFull == null
                                    ? "Target not set"
                                    : `${stats.idealCaseload} weekly target · ${stats.percentFull}% full`}
                                {" · "}
                                {stats.heldClients} held
                            </span>
                        </div>
                    );
                },
            },
            {
                Header: "Years with HT",
                id: "years_with_dsp",
                accessor: (row: IClinician) => {
                    const momentTime = moment();
                    const months = momentTime.diff(row.start_date, "months", true);
                    const years = momentTime.diff(row.start_date, "years");
                    const display =
                        years > 0 ? years : months >= 6 && months < 1 ? 0.5 : 0;
                    return display;
                },
            },
            {
                Header: "",
                id: "actions",
                accessor: (row: IClinician) => (
                    <OverlayTrigger
                        trigger="click"
                        placement="bottom-end"
                        flip
                        rootClose
                        show={actionsClinicianEmail === row.email}
                        onToggle={(show) =>
                            setActionsClinicianEmail(show ? row.email : null)
                        }
                        overlay={
                            <Popover
                                id={`clinician-actions-${row.id ?? row.email}`}
                                className={styles.actionsPopover}
                                aria-label={`Actions for ${row.fullname}`}
                            >
                                <Popover.Content
                                    className={styles.actionsDropdownContent}
                                    onClick={() => setActionsClinicianEmail(null)}
                                >
                                    <Button
                                        variant="normal"
                                        type="button"
                                        onClick={() => editClinician(row)}
                                    >
                                        Edit clinician
                                    </Button>
                                    <Button
                                        variant="normal"
                                        type="button"
                                        onClick={() => {
                                            history.push("/v2/office", {
                                                selectedLocation: row.locations[0],
                                                selectedClinicianId: row.email,
                                            });
                                        }}
                                    >
                                        Set office hours
                                    </Button>
                                    <Button
                                        variant="normal"
                                        type="button"
                                        onClick={() => {
                                            history.push("/v2/intake-c-a", {
                                                clinicianEmail: row.email,
                                            });
                                        }}
                                    >
                                        Manage availability
                                    </Button>
                                    {row.is_supervisor && (
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={() => {
                                                history.push("/v2/supervisees", {
                                                    supervisorEmail: row.email,
                                                });
                                            }}
                                        >
                                            View Supervisees
                                        </Button>
                                    )}

                                    <Button
                                        variant="normal"
                                        type="button"
                                        onClick={async () => {
                                            const exportCaseloadFunction =
                                                httpsCallable(
                                                    fxs,
                                                    "httpFunctionExportClinicianCaseload"
                                                );
                                            try {
                                                await exportCaseloadFunction({
                                                    clinician_id:
                                                        row.id || row.email,
                                                });
                                                addToast(
                                                    <p>
                                                        Clinician caseload export has
                                                        started. Check{" "}
                                                        <a
                                                            href="https://docs.google.com/spreadsheets/d/1Y21waUa27mU6M4YFrwtrgPDkfriXXDHbo8xDRYqJYPE"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            this link
                                                        </a>{" "}
                                                        to view the exported caseload
                                                    </p>,
                                                    {
                                                        autoDismiss: false,
                                                        appearance: "success",
                                                    }
                                                );
                                            } catch (ex) {
                                                console.error(ex);
                                                addToast(
                                                    "Error occurred while exporting caseload",
                                                    {
                                                        appearance: "error",
                                                    }
                                                );
                                            }
                                        }}
                                    >
                                        Export Caseload
                                    </Button>

                                    <Button
                                        variant="normal"
                                        type="button"
                                        onClick={() => {
                                            setShowDeleteDialog(true);
                                            setClinicianInView(row);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </Popover.Content>
                            </Popover>
                        }
                    >
                        <Button
                            variant="secondary"
                            onClick={(e) => e.stopPropagation()}
                            iconRight={<ChevronDownIcon stroke="currentColor" />}
                            aria-expanded={actionsClinicianEmail === row.email}
                        >
                            Actions
                        </Button>
                    </OverlayTrigger>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clinicianList,
        history,
        addToast,
        allExperienceLevelOptions,
        allDepartmentsAndServices,
        actionsClinicianEmail,
    ]);

    React.useEffect(() => {
        fetchClinicians();
    }, [fetchClinicians]);

    React.useEffect(() => {
        store
            .getAllMethodologyOptions()
            .then((options) => setMethodologyOptions(options))
            .catch(() => {});
        store
            .getAllExperienceLevels()
            .then(setAllExperienceLevelOptions)
            .catch(() =>
                addToast("Unable to load credential expiration information", {
                    appearance: "error",
                })
            );
    }, [addToast]);

    const searchCliniciansByName = async () => {
        const clinicianName = searchClinicianName.trim();
        if (!clinicianName) {
            return;
        }
        try {
            const foundClinicians =
                (await store.findClinicianByField("firstname", clinicianName)) || [];
            if (foundClinicians.length < 1) {
                addToast(
                    `No clinicians found with the first name ${clinicianName}`,
                    {
                        appearance: "error",
                    }
                );
            }
            setClinicianList(foundClinicians);
        } catch (error) {
            addToast("Unable to search clinicians. Please try again.", {
                appearance: "error",
            });
        }
    };

    const handleClinicianSearchSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();
        await searchCliniciansByName();
    };

    const tableHeader = (
        <div className={styles.tableHeader}>
            <Dropdown>
                <Dropdown.Trigger asChild>
                    <FilterButton text="Show only" />
                </Dropdown.Trigger>
                <Dropdown.Content>
                    <div className={styles.filterDropdown}>
                        <Dropdown.Item
                            onClick={async () => {
                                setSearchClincianName("");
                                await fetchOnlyPausedClinicians();
                            }}
                        >
                            Paused Clinicians
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={async () => {
                                setSearchClincianName("");
                                await fetchonlyPrioritizedClinicians();
                            }}
                        >
                            Clinicians with Priority
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={async () => {
                                setSearchClincianName("");
                                await fetchonlyDoNotShowOnWebsiteClinicians();
                            }}
                        >
                            Clinicians with Do not show on Website
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={async () => {
                                setSearchClincianName("");
                                await fetchonlyDoNotSyncToZocClinicians();
                            }}
                        >
                            Clinicians with Do not sync to Zocdoc
                        </Dropdown.Item>
                    </div>
                </Dropdown.Content>
            </Dropdown>
            <form
                className={styles.clinicianSearchForm}
                onSubmit={handleClinicianSearchSubmit}
            >
                <SearchInput
                    value={searchClinicianName}
                    placeholder="Clinician first name"
                    onChange={(e) => setSearchClincianName(e.target.value)}
                />
                <Button type="submit" size="icon" title="Search" aria-label="Search">
                    <SearchIcon />
                </Button>
            </form>
            <Button
                onClick={async () => {
                    setSearchClincianName("");
                    await fetchClinicians();
                }}
                variant="secondary"
            >
                Reset
            </Button>
            <div className={styles.tableHeaderActions}>
                <Button onClick={addClinician}>Add clinician</Button>
            </div>
        </div>
    );

    const tableFooter = (
        <TablePagination
            currentPage={pageNumber + 1}
            onFirstPage={() => {
                fetchClinicians(undefined, undefined, 0);
            }}
            onPrevPage={() => {
                fetchClinicians(
                    undefined,
                    clinicianList[0].firstname,
                    pageNumber - 1
                );
            }}
            onNextPage={() => {
                fetchClinicians(
                    clinicianList[clinicianList.length - 1].firstname,
                    undefined,
                    pageNumber + 1
                );
            }}
            isNextDisabled={clinicianList.length < PAGE_LIMIT}
            showPrevButton={pageNumber > 1}
            pageNumberText={`Page ${pageNumber + 1}`}
            buttonClassName={styles.tableFooterButton}
        />
    );

    return (
        <div className={cn(styles.manageClinician, "v2-page")}>
            <AlertDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                title="Delete clinician?"
                isDeleting
                saveText="Delete"
                description="Are you sure you want to delete this clinician? This action cannot be reversed."
                onSaveClick={() => deleteClinician()}
            />
            {isEditorOpen ? (
                <div className={styles.filtersWrapper}>
                    <div className={styles.header}>
                        <div>
                            <p className={styles.title}>
                                {clinicianInView
                                    ? "Edit clinician"
                                    : "Add clinician"}
                            </p>
                            {clinicianInView && (
                                <p className={styles.editorSubtitle}>
                                    {clinicianInView.fullname}
                                </p>
                            )}
                        </div>
                        <Button variant="secondary" onClick={closeEditor}>
                            Back to clinicians
                        </Button>
                    </div>
                    <form
                        onSubmit={submitClinicianData}
                        className={cn(styles.filters, "v2-form")}
                    >
                        {clinicianData.fullname && (
                            <div className={styles.clinicianInfo}>
                                <p className={styles.clinicianName}>
                                    {clinicianData.fullname}
                                </p>

                                <span>
                                    <Badge
                                        content={clinicianData.credential}
                                        textColor={
                                            getV2BadgePropsByPractice(
                                                clinicianData.credential
                                            ).textColor
                                        }
                                        bgColor={
                                            getV2BadgePropsByPractice(
                                                clinicianData.credential
                                            ).bgColor
                                        }
                                    />
                                    <CredentialExpirationStatus
                                        clinician={clinicianData}
                                        experienceLevelOptions={
                                            allExperienceLevelOptions
                                        }
                                    />
                                </span>
                            </div>
                        )}
                        <Input
                            placeholder="Clinician's email"
                            label="Email Address"
                            value={clinicianData.email}
                            onChange={(e) =>
                                updateClinicianDataState("email", e.target.value)
                            }
                            isRequired
                            disabled={Boolean(clinicianData.id)}
                            hasError={
                                clinicianData.id
                                    ? validateForm().missingFields.includes("email")
                                    : false
                            }
                            errorText="Email address is required"
                            type="email"
                        />
                        <Select
                            options={locations}
                            placeholder="Select location"
                            label="Location"
                            value={locations.filter((option) => {
                                return clinicianData.locations.includes(
                                    option.value
                                );
                            })}
                            onChange={(value) =>
                                updateClinicianDataState(
                                    "locations",
                                    value as Option[]
                                )
                            }
                            isRequired
                            hasError={
                                clinicianData.id
                                    ? validateForm().missingFields.includes(
                                          "locations"
                                      )
                                    : false
                            }
                            errorText="Location is required"
                            isMulti
                            hideSelectedOptions
                            multiHasValues
                        />
                        <Input
                            placeholder="Clinician's first name"
                            label="First name"
                            value={clinicianData.firstname}
                            onChange={(e) =>
                                updateClinicianDataState("firstname", e.target.value)
                            }
                            isRequired
                            hasError={
                                clinicianData.id
                                    ? validateForm().missingFields.includes(
                                          "firstname"
                                      )
                                    : false
                            }
                            errorText="First name is required"
                        />
                        <Input
                            placeholder="Clinician's last name"
                            label="Last name"
                            value={clinicianData.lastname}
                            onChange={(e) =>
                                updateClinicianDataState("lastname", e.target.value)
                            }
                            isRequired
                            hasError={
                                clinicianData.id
                                    ? validateForm().missingFields.includes(
                                          "lastname"
                                      )
                                    : false
                            }
                            errorText="Last name is required"
                        />
                        <Select
                            options={credentials}
                            placeholder="Select credential"
                            label="Credential"
                            value={credentials.find(
                                (option) => option.value === clinicianData.credential
                            )}
                            onChange={(value) =>
                                updateClinicianDataState(
                                    "credential",
                                    (value as Option).value
                                )
                            }
                            isRequired
                            hasError={
                                clinicianData.id
                                    ? validateForm().missingFields.includes(
                                          "credential"
                                      )
                                    : false
                            }
                            errorText="Credential is required"
                        />
                        <Input
                            placeholder="Clinician's NPI"
                            label="NPI"
                            value={clinicianData.npi}
                            onChange={(e) =>
                                updateClinicianDataState("npi", e.target.value)
                            }
                            isRequired
                            hasError={
                                clinicianData.id
                                    ? validateForm().missingFields.includes("npi")
                                    : false
                            }
                            errorText="NPI is required"
                        />
                        <PhoneInput
                            placeholder="(xxx) xxx-xxxx"
                            label="Contact info for client"
                            value={formatLegacyPhoneNumber(
                                clinicianData.contact_info_client || ""
                            )}
                            onChange={(phoneNo: string | undefined) =>
                                updateClinicianDataState(
                                    "contact_info_client",
                                    phoneNo || ""
                                )
                            }
                        />
                        <PhoneInput
                            placeholder="(xxx) xxx-xxxx"
                            label="Contact info (Personal)"
                            value={formatLegacyPhoneNumber(
                                clinicianData.contact_info_personal || ""
                            )}
                            onChange={(phoneNo: string | undefined) =>
                                updateClinicianDataState(
                                    "contact_info_personal",
                                    phoneNo || ""
                                )
                            }
                        />
                        <div className={styles.supervisorWrapper}>
                            <Select
                                options={supervisorOptions}
                                placeholder="Select supervisor"
                                label="Choose Primary Supervisor"
                                value={supervisorOptions.find(
                                    (option) =>
                                        option.value === clinicianData.supervisor_id
                                )}
                                onChange={(value) =>
                                    updateClinicianDataState(
                                        "supervisor_id",
                                        (value as Option).value
                                    )
                                }
                                isLongListInDialog
                                isSearchable
                            />
                            {clinicianData?.supervisor_id &&
                                clinicianData?.departments && (
                                    <div className={styles.departmentWrapper}>
                                        <div className={styles.departmentLabel}>
                                            <Badge
                                                content={
                                                    clinicianData.departments[0]
                                                }
                                                textColor={
                                                    getV2BadgePropsByPractice(
                                                        clinicianData.departments[0]
                                                    ).textColor
                                                }
                                                bgColor={
                                                    getV2BadgePropsByPractice(
                                                        clinicianData.departments[0]
                                                    ).bgColor
                                                }
                                            />
                                        </div>
                                        <Button
                                            variant="normal"
                                            className={styles.removeButton}
                                            onClick={() => {
                                                updateClinicianDataState(
                                                    "supervisor_id",
                                                    ""
                                                );
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                )}
                        </div>
                        {clinicianInView?.secondary_supervisor_ids?.map(
                            ({ supervisor_id, practice_id }) => {
                                const practiceName = getPracticeName(practice_id);

                                // Function to handle the removal of a specific practice
                                const handleRemovePractice = () => {
                                    const updatedSecondarySupervisors =
                                        clinicianInView?.secondary_supervisor_ids?.filter(
                                            (item) =>
                                                item.practice_id !== practice_id
                                        );

                                    // Update clinicianInView state with the updated array
                                    updateClinicianDataState(
                                        "secondary_supervisor_ids",
                                        updatedSecondarySupervisors || []
                                    );

                                    setClinicianInView({
                                        ...clinicianInView,
                                        secondary_supervisor_ids:
                                            updatedSecondarySupervisors,
                                    });
                                };
                                // function to allow users edit secondary supervisors
                                const handleSecondarySupervisorChange = async (
                                    selectedSupervisorId
                                ) => {
                                    // Find the index of the supervisor to update
                                    const indexToUpdate =
                                        clinicianInView?.secondary_supervisor_ids?.findIndex(
                                            (item) =>
                                                item.practice_id === practice_id
                                        ) || 0;

                                    if (indexToUpdate !== -1) {
                                        // Create updated array with the selected supervisor_id
                                        const updatedSecondarySupervisors = [
                                            ...(clinicianInView?.secondary_supervisor_ids ||
                                                []),
                                        ];
                                        updatedSecondarySupervisors[indexToUpdate] =
                                            {
                                                practice_id,
                                                supervisor_id: selectedSupervisorId,
                                            };

                                        // Update clinicianInView state with the updated array
                                        updateClinicianDataState(
                                            "secondary_supervisor_ids",
                                            updatedSecondarySupervisors
                                        );

                                        setClinicianInView({
                                            ...clinicianInView,
                                            secondary_supervisor_ids:
                                                updatedSecondarySupervisors,
                                        });
                                    }
                                };

                                return (
                                    <div
                                        key={practice_id}
                                        className={styles.supervisorWrapper}
                                    >
                                        <Select
                                            options={supervisorOptions}
                                            placeholder="Select supervisor"
                                            label="Secondary supervisor(s)"
                                            value={supervisorOptions.find(
                                                (option) =>
                                                    option.value === supervisor_id
                                            )}
                                            onChange={(value) =>
                                                handleSecondarySupervisorChange(
                                                    (value as Option).value
                                                )
                                            }
                                            isLongListInDialog
                                        />

                                        <div className={styles.departmentWrapper}>
                                            <div className={styles.departmentLabel}>
                                                <Badge
                                                    content={practiceName}
                                                    textColor={
                                                        getV2BadgePropsByPractice(
                                                            practiceName
                                                        ).textColor
                                                    }
                                                    bgColor={
                                                        getV2BadgePropsByPractice(
                                                            practiceName
                                                        ).bgColor
                                                    }
                                                />
                                            </div>

                                            <Button
                                                variant="normal"
                                                className={styles.removeButton}
                                                onClick={handleRemovePractice}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                );
                            }
                        )}

                        {(supervisorPracticePairs.length > 0 ||
                            Object.entries(getFilteredDepartments())?.length >
                                0) && (
                            <div className={styles.supervisorPracticePairs}>
                                {supervisorPracticePairs.map((pair, index) => (
                                    <div key={index}>
                                        <div
                                            className={styles.supervisorPracticePair}
                                        >
                                            <Select
                                                wrapperClass={
                                                    styles.supervisorSelect
                                                }
                                                options={supervisorOptions}
                                                placeholder="Select supervisor"
                                                value={supervisorOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        pair.supervisor_id
                                                )}
                                                onChange={(value) =>
                                                    handleSupervisorChange(
                                                        index,
                                                        (value as Option).value
                                                    )
                                                }
                                                isLongListInDialog
                                                hasError={
                                                    validateForm()
                                                        .hasIncompleteSupervisorPairs &&
                                                    pair.supervisor_id === ""
                                                }
                                                errorText="Supervisor is required"
                                            />

                                            <Select
                                                wrapperClass={styles.practiceSelect}
                                                options={practiceOptions}
                                                placeholder="Select Practice"
                                                value={practiceOptions.find(
                                                    (option) =>
                                                        option.value ===
                                                        pair.practice_id
                                                )}
                                                onChange={(value) =>
                                                    handlePracticeChange(
                                                        index,
                                                        (value as Option).value
                                                    )
                                                }
                                            />
                                            <Button
                                                variant="normal"
                                                className={styles.removePairButton}
                                                aria-label="Remove supervisor and practice"
                                                onClick={() => removePair(index)}
                                            >
                                                <CancelIcon />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                {Object.entries(getFilteredDepartments())?.length >
                                    0 && (
                                    <Button
                                        variant="primary"
                                        onClick={addMorePair}
                                        className={styles.addButton}
                                    >
                                        Add Supervisor
                                    </Button>
                                )}
                            </div>
                        )}
                        <FormCheckbox
                            label="Make supervisor"
                            checked={clinicianData.is_supervisor}
                            onChange={() =>
                                updateClinicianDataState(
                                    "is_supervisor",
                                    !clinicianData.is_supervisor
                                )
                            }
                            description="Check this box to make this clinician one of the supervisors"
                        />
                        <div>
                            <label className={styles.personalityTraitsLabel}>
                                Intake - Other
                            </label>
                            <div className={styles.personalityTraits}>
                                {methodologyOptions.map((value: string) => {
                                    const checked =
                                        clinicianData.methodology?.includes(value);
                                    return (
                                        <FormCheckbox
                                            onChange={() => {
                                                updateClinicianDataState(
                                                    "methodology",
                                                    value
                                                );
                                            }}
                                            checked={checked}
                                            label={value}
                                            key={value}
                                            id={`inline-${value}-1`}
                                            noBorder
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <TextArea
                            label="Clinician Notes"
                            value={clinicianData.personality_notes}
                            onChange={(e) =>
                                updateClinicianDataState(
                                    "personality_notes",
                                    e.target.value
                                )
                            }
                        />
                        <DatePicker
                            label="Date of birth"
                            selected={clinicianData.dob}
                            onChange={(date) =>
                                updateClinicianDataState("dob", date as Date)
                            }
                            dateFormat="MM/dd/yyyy"
                        />
                        <DatePicker
                            label="Date started with HT"
                            selected={clinicianData.start_date}
                            onChange={(date) =>
                                updateClinicianDataState("start_date", date as Date)
                            }
                            dateFormat="MM/dd/yyyy"
                        />
                        <DatePicker
                            label="Started practicing"
                            selected={clinicianData.started_practicing}
                            onChange={(date) =>
                                updateClinicianDataState(
                                    "started_practicing",
                                    date as Date
                                )
                            }
                            dateFormat="MM/dd/yyyy"
                        />
                        <FormCheckbox
                            label="Is Priority"
                            checked={clinicianData.marked_as_priority}
                            onChange={() => {
                                const nextPriority =
                                    !clinicianData.marked_as_priority;
                                updateClinicianDataState(
                                    "marked_as_priority",
                                    nextPriority
                                );
                                if (nextPriority === originalPriority) {
                                    setPriorityChangeNote("");
                                }
                            }}
                            description="Checking this box will mark clinician as a priority on The Find Clinicians page"
                        />
                        {priorityWasChanged && (
                            <TextArea
                                label={
                                    clinicianData.marked_as_priority
                                        ? "Reason for adding to priority"
                                        : "Reason for removing from priority"
                                }
                                name="priority_change_note"
                                value={priorityChangeNote}
                                onChange={(event) =>
                                    setPriorityChangeNote(event.target.value)
                                }
                                required
                            />
                        )}
                        <FormCheckbox
                            label="Pause Bookings"
                            checked={clinicianData.booking_paused}
                            onChange={() =>
                                setClinicianData((previous) => ({
                                    ...previous,
                                    booking_paused: !previous.booking_paused,
                                    booking_pause_reason: wasBookingsPaused
                                        ? previous.booking_pause_reason
                                        : "",
                                }))
                            }
                            description="Checking this box will display a 'DO NOT BOOK' warning when this clinician appears on the search result."
                        />
                        {clinicianData.booking_paused && !wasBookingsPaused && (
                            <TextArea
                                label="Reason for pausing bookings"
                                name="booking_pause_reason"
                                value={clinicianData.booking_pause_reason ?? ""}
                                onChange={(event) =>
                                    updateClinicianDataState(
                                        "booking_pause_reason",
                                        event.target.value
                                    )
                                }
                                required
                            />
                        )}
                        <FormCheckbox
                            label="Do not show on Website"
                            checked={clinicianData.do_not_show_on_website}
                            onChange={() =>
                                updateClinicianDataState(
                                    "do_not_show_on_website",
                                    !clinicianData.do_not_show_on_website
                                )
                            }
                            description="Checking this box will stop this Clinician from showing up as available on HT website"
                        />
                        <FormCheckbox
                            label="Do not sync to Zocdoc"
                            checked={clinicianData.do_not_sync_to_zocdoc}
                            onChange={() =>
                                updateClinicianDataState(
                                    "do_not_sync_to_zocdoc",
                                    !clinicianData.do_not_sync_to_zocdoc
                                )
                            }
                            description=" Checking this box will stop this Clinician availability from syncing to Zodoc"
                        />

                        {clinicianInView?.email === clinicianData.email && (
                            <ClinicianStatusHistory changes={statusChanges} />
                        )}
                        <div className={styles.formActions}>
                            <Button variant="secondary" onClick={closeEditor}>
                                Cancel
                            </Button>
                            <Button type="submit">
                                {clinicianInView
                                    ? "Save clinician"
                                    : "Create clinician"}
                            </Button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className={styles.cliniciansWrapper}>
                    <Table
                        columns={columns}
                        data={clinicianList}
                        allowSelect={false}
                        header={tableHeader}
                        footer={tableFooter}
                        hasActionsColumn={true}
                        stickyLastColumn
                        handleRowStyle={(row) => getClinicianRowStyle(row.original)}
                    />
                </div>
            )}
        </div>
    );
}
